import React, {useState} from "react";
import {
    Box,
    Paper,
    Typography,
    Button,
    Divider,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import {useNavigate} from "react-router";
import {SeoHead} from "../app/SeoHead";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {BookOrderForm} from "./BookOrderForm";
import {BookPriceEditDialog} from "./BookPriceEditDialog";
import {useBookPricing} from "../firebase/useBookPricing";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";

export const OrderBooks = () => {
    const navigate = useNavigate();
    const [orderReceipt, setOrderReceipt] = useState(null);
    const [editPriceOpen, setEditPriceOpen] = useState(false);
    const {price, updatePrice} = useBookPricing();
    const isAdmin = useCheckIfAdmin();

    return (
        <Box
            component="div"
            sx={{
                width: "100%",
                maxWidth: 1200,
                mx: "auto",
                px: 0,
                py: {xs: 2, md: 4},
                minWidth: 0,
                boxSizing: "border-box",
            }}
        >
            <SeoHead
                title="Bestill kursmateriell"
                description="Bestilling av arbeidsbøker til Kurs i Bekymringsmestring for sertifiserte kursholdere."
                path="/order-books"
            />

            {/* Back to home button */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/")}
                sx={{
                    mb: 3,
                    color: "primary.main",
                    fontWeight: 600,
                    textTransform: "none",
                    minHeight: 44,
                    "&:hover": {bgcolor: "rgba(40, 82, 56, 0.08)"},
                    "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                }}
            >
                Tilbake til forsiden
            </Button>

            {orderReceipt ? (
                /* Confirmation & Order Receipt View */
                <Paper
                    elevation={0}
                    sx={{
                        maxWidth: 720,
                        mx: "auto",
                        p: {xs: 3, sm: 5},
                        borderRadius: 4,
                        border: "1px solid",
                        borderColor: "rgba(40, 82, 56, 0.2)",
                        bgcolor: "background.paper",
                        textAlign: "center",
                    }}
                >
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 72,
                            height: 72,
                            borderRadius: "50%",
                            bgcolor: "rgba(40, 82, 56, 0.1)",
                            color: "primary.main",
                            mb: 2.5,
                        }}
                    >
                        <CheckCircleOutlineIcon sx={{fontSize: 44}} />
                    </Box>

                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{fontWeight: 800, color: "text.primary", mb: 1, letterSpacing: "-0.02em"}}
                    >
                        Takk for din bestilling!
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{mb: 4, maxWidth: 540, mx: "auto"}}>
                        En bekreftelse er sendt til <strong>{orderReceipt.email}</strong>. Vi behandler ordren din fortløpende.
                    </Typography>

                    {/* Receipt Details Box */}
                    <Box
                        sx={{
                            textAlign: "left",
                            p: 3,
                            borderRadius: 3,
                            bgcolor: "rgba(240, 244, 241, 0.6)",
                            border: "1px solid rgba(40, 82, 56, 0.12)",
                            mb: 4,
                        }}
                    >
                        <Typography variant="subtitle2" sx={{fontWeight: 700, color: "primary.main", mb: 1.5, textTransform: "uppercase", letterSpacing: 0.5}}>
                            Bestillingskvittering
                        </Typography>

                        <Box sx={{display: "flex", justifyContent: "space-between", py: 0.75}}>
                            <Typography variant="body2" color="text.secondary">
                                Kursmateriell:
                            </Typography>
                            <Typography variant="body2" sx={{fontWeight: 600}}>
                                Arbeidsbok i Bekymringsmestring
                            </Typography>
                        </Box>

                        <Box sx={{display: "flex", justifyContent: "space-between", py: 0.75}}>
                            <Typography variant="body2" color="text.secondary">
                                Antall:
                            </Typography>
                            <Typography variant="body2" sx={{fontWeight: 600}}>
                                {orderReceipt.numberOfBooks} bøker
                            </Typography>
                        </Box>

                        <Box sx={{display: "flex", justifyContent: "space-between", py: 0.75}}>
                            <Typography variant="body2" color="text.secondary">
                                Totalsum (inkl. frakt):
                            </Typography>
                            <Typography variant="body2" sx={{fontWeight: 700, color: "primary.main"}}>
                                {orderReceipt.totalAmount} kr
                            </Typography>
                        </Box>

                        {orderReceipt.delivery && (
                            <>
                                <Divider sx={{my: 1.5}} />
                                <Box sx={{py: 0.75}}>
                                    <Typography variant="caption" color="text.secondary" sx={{display: "block", mb: 0.5}}>
                                        Leveringsadresse:
                                    </Typography>
                                    <Typography variant="body2" sx={{fontWeight: 600}}>
                                        {orderReceipt.delivery.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {orderReceipt.delivery.street}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {orderReceipt.delivery.areaCode} {orderReceipt.delivery.place}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Box>

                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => setOrderReceipt(null)}
                        sx={{
                            borderRadius: 3,
                            px: 3,
                            py: 1,
                            minHeight: 44,
                            textTransform: "none",
                            fontWeight: 600,
                            "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                        }}
                    >
                        Bestill flere bøker
                    </Button>
                </Paper>
            ) : (
                /* Order Form and Book Presentation View */
                <>
                    {/* Header Banner */}
                    <Box sx={{mb: {xs: 3, md: 5}, textAlign: {xs: "left", sm: "center"}}}>
                        <Chip
                            icon={<MenuBookIcon sx={{fontSize: "1rem !important"}} />}
                            label="Kursmateriell for fagfolk"
                            size="small"
                            sx={{
                                mb: 1.5,
                                bgcolor: "rgba(40, 82, 56, 0.08)",
                                color: "primary.main",
                                fontWeight: 700,
                                borderRadius: 2,
                            }}
                        />
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                fontWeight: 800,
                                color: "text.primary",
                                fontSize: {xs: "1.85rem", sm: "2.5rem"},
                                letterSpacing: "-0.02em",
                                mb: 1,
                            }}
                        >
                            Bestill kursmateriell
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            sx={{
                                maxWidth: 680,
                                mx: {xs: 0, sm: "auto"},
                                fontSize: {xs: "0.95rem", sm: "1.1rem"},
                                lineHeight: 1.5,
                            }}
                        >
                            Offisielt kursmateriell for sertifiserte kursholdere i Bekymringsmestring. Bøkene benyttes som arbeidsbøker for kursdeltakere og pasienter.
                        </Typography>
                    </Box>

                    {/* Responsive Grid: Book Presentation (Left) + Form (Right) */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {xs: "1fr", md: "380px 1fr"},
                            gap: {xs: 3, md: 4},
                            alignItems: "start",
                        }}
                    >
                        {/* Book Presentation Sidebar */}
                        <Box sx={{display: "flex", flexDirection: "column", gap: 3}}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: {xs: 3, sm: 3.5},
                                    borderRadius: 3.5,
                                    border: "1px solid",
                                    borderColor: "rgba(40, 82, 56, 0.15)",
                                    bgcolor: "background.paper",
                                }}
                            >
                                {/* Book Cover Presentation Badge */}
                                <Box
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        bgcolor: "primary.main",
                                        color: "common.white",
                                        mb: 3,
                                        textAlign: "center",
                                        boxShadow: "0 8px 24px rgba(40, 82, 56, 0.2)",
                                        position: "relative",
                                        overflow: "hidden",
                                    }}
                                >
                                    <MenuBookIcon sx={{fontSize: 48, opacity: 0.9, mb: 1}} />
                                    <Typography variant="h6" sx={{fontWeight: 800, lineHeight: 1.25, mb: 0.5}}>
                                        Arbeidsbok for kursdeltakere
                                    </Typography>
                                    <Typography variant="caption" sx={{opacity: 0.85, letterSpacing: 0.5, textTransform: "uppercase"}}>
                                        Kurs i Bekymringsmestring
                                    </Typography>
                                </Box>

                                {/* Price Box */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        flexWrap: "wrap",
                                        gap: 1,
                                        pb: 2,
                                        mb: 2,
                                        borderBottom: "1px solid rgba(40, 82, 56, 0.12)",
                                    }}
                                >
                                    <div>
                                        <Typography variant="caption" color="text.secondary" sx={{display: "block"}}>
                                            Fast pris per eksemplar
                                        </Typography>
                                        <Typography variant="h5" sx={{fontWeight: 800, color: "primary.main"}}>
                                            {price} kr
                                        </Typography>
                                    </div>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                        <Chip
                                            label="Inkl. frakt"
                                            size="small"
                                            sx={{
                                                bgcolor: "rgba(40, 82, 56, 0.1)",
                                                color: "primary.main",
                                                fontWeight: 700,
                                            }}
                                        />
                                        {isAdmin && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<EditOutlinedIcon fontSize="small" />}
                                                onClick={() => setEditPriceOpen(true)}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    minHeight: 36,
                                                    "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                                                }}
                                            >
                                                Endre pris
                                            </Button>
                                        )}
                                    </Box>
                                </Box>

                                {/* Highlights list */}
                                <List disablePadding sx={{display: "flex", flexDirection: "column", gap: 1}}>
                                    <ListItem disableGutters disablePadding sx={{alignItems: "flex-start", gap: 1.25}}>
                                        <ListItemIcon sx={{minWidth: "auto", mt: 0.3, color: "primary.main"}}>
                                            <VerifiedOutlinedIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Kun for sertifiserte kursholdere"
                                            secondary="Bøkene selges videre til deltakere i dine kurs eller pasientgrupper."
                                            primaryTypographyProps={{variant: "body2", fontWeight: 700}}
                                            secondaryTypographyProps={{variant: "caption"}}
                                        />
                                    </ListItem>

                                    <ListItem disableGutters disablePadding sx={{alignItems: "flex-start", gap: 1.25}}>
                                        <ListItemIcon sx={{minWidth: "auto", mt: 0.3, color: "primary.main"}}>
                                            <LocalShippingOutlinedIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Rask dørlevering"
                                            secondary="Sendes til gateadresse (ikke postboks) innen få virkedager."
                                            primaryTypographyProps={{variant: "body2", fontWeight: 700}}
                                            secondaryTypographyProps={{variant: "caption"}}
                                        />
                                    </ListItem>

                                    <ListItem disableGutters disablePadding sx={{alignItems: "flex-start", gap: 1.25}}>
                                        <ListItemIcon sx={{minWidth: "auto", mt: 0.3, color: "primary.main"}}>
                                            <ReceiptLongOutlinedIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Faktura med referanse"
                                            secondary="Oppgi ressursnummer eller virksomhetsreferanse for smidig attestasjon."
                                            primaryTypographyProps={{variant: "body2", fontWeight: 700}}
                                            secondaryTypographyProps={{variant: "caption"}}
                                        />
                                    </ListItem>
                                </List>
                            </Paper>

                            {/* Help & Contact Notice */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 3,
                                    bgcolor: "rgba(240, 244, 241, 0.5)",
                                    border: "1px dashed rgba(40, 82, 56, 0.2)",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1.5,
                                }}
                            >
                                <MailOutlineIcon sx={{color: "primary.main", mt: 0.2}} />
                                <Box>
                                    <Typography variant="body2" sx={{fontWeight: 700, mb: 0.5}}>
                                        Spørsmål om bestillingen?
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Ta kontakt med oss på{" "}
                                        <a
                                            href="mailto:post@bekymringsmestring.no"
                                            style={{color: "#285238", fontWeight: 600}}
                                        >
                                            post@bekymringsmestring.no
                                        </a>
                                        .
                                    </Typography>
                                </Box>
                            </Paper>
                        </Box>

                        {/* Order Form Column */}
                        <Box sx={{minWidth: 0}}>
                            <BookOrderForm
                                pricePerBook={price}
                                onSuccess={(data) => setOrderReceipt(data)}
                            />
                        </Box>
                    </Box>

                    {isAdmin && (
                        <BookPriceEditDialog
                            open={editPriceOpen}
                            currentPrice={price}
                            onClose={() => setEditPriceOpen(false)}
                            onSavePrice={updatePrice}
                        />
                    )}
                </>
            )}
        </Box>
    );
};